import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Copy, Users, Gift, TrendingUp } from "lucide-react";
import { toast } from "sonner";

// Referral panel — shown on the CompanyDashboard. Encourages SMEs to invite
// teachers/schools: when a referred teacher creates their FIRST class, the
// SME automatically gets +30 days of Boost on all their companies.
export default function ReferralPanel() {
  const { lang } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    http.get("/referral/mine")
      .then((r) => setData(r.data))
      .catch(() => setData(false))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return null;
  if (data === false) return null; // Endpoint returned 403 (non-company user)

  const copy = () => {
    navigator.clipboard.writeText(data.url);
    toast.success(lang === "fr" ? "Lien copié !" : "Link copied!");
  };

  return (
    <section className="mt-10" data-testid="referral-panel">
      <div className="rounded-3xl bg-gradient-to-br from-[#FFF7ED] via-white to-[#FFEDD5] border border-[#F97316]/30 p-6 md:p-8 relative overflow-hidden">
        <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-[#F97316]/15 blur-3xl" />
        <div className="relative">
          <div className="flex items-center gap-2 mb-2">
            <Gift className="w-5 h-5 text-[#F97316]" />
            <p className="text-xs uppercase tracking-[0.2em] text-[#C2410C] font-semibold">
              {lang === "fr" ? "Programme parrainage" : "Referral program"}
            </p>
          </div>
          <h3 className="font-display text-2xl font-bold text-[#1E3A8A]">
            {lang === "fr" ? "Invitez une école, gagnez 30 jours de Boost" : "Invite a school, earn 30 days of Boost"}
          </h3>
          <p className="text-sm text-slate-600 mt-2 max-w-2xl">
            {lang === "fr"
              ? "Partagez ce lien à un·e enseignant·e. Dès qu'il ou elle crée sa première classe sur Proxijob, +30 jours de Boost sont automatiquement crédités à toutes vos entreprises."
              : "Share this link with a teacher. As soon as they create their first class on Proxijob, +30 days of Boost are automatically credited to all your companies."}
          </p>

          <div className="mt-5 flex items-center gap-2 rounded-full bg-white border border-slate-200 pl-4 pr-1 py-1 max-w-2xl shadow-sm">
            <span className="text-sm font-mono text-slate-700 truncate flex-1" data-testid="referral-url">{data.url}</span>
            <Button size="sm" onClick={copy} data-testid="referral-copy-btn" className="bg-[#1E3A8A] hover:bg-[#172554] rounded-full h-9 px-4 shrink-0">
              <Copy className="w-3.5 h-3.5 mr-1.5" /> {lang === "fr" ? "Copier" : "Copy"}
            </Button>
          </div>

          <div className="mt-6 grid grid-cols-3 gap-3 max-w-2xl">
            <div className="rounded-xl bg-white border border-slate-200 p-3">
              <Users className="w-3.5 h-3.5 text-[#1E3A8A] mb-1" />
              <p className="text-2xl font-display font-bold text-slate-900" data-testid="referral-signups">{data.referred_signups}</p>
              <p className="text-[10px] uppercase tracking-wide text-slate-500 mt-0.5">
                {lang === "fr" ? "Enseignants inscrits" : "Teachers signed up"}
              </p>
            </div>
            <div className="rounded-xl bg-white border border-slate-200 p-3">
              <TrendingUp className="w-3.5 h-3.5 text-emerald-600 mb-1" />
              <p className="text-2xl font-display font-bold text-slate-900" data-testid="referral-classes">{data.classes_created}</p>
              <p className="text-[10px] uppercase tracking-wide text-slate-500 mt-0.5">
                {lang === "fr" ? "Classes créées" : "Classes created"}
              </p>
            </div>
            <div className="rounded-xl bg-gradient-to-br from-[#F97316] to-[#EA580C] text-white p-3">
              <Gift className="w-3.5 h-3.5 mb-1" />
              <p className="text-2xl font-display font-bold" data-testid="referral-days">{data.boost_days_earned}</p>
              <p className="text-[10px] uppercase tracking-wide text-white/90 mt-0.5">
                {lang === "fr" ? "Jours de Boost gagnés" : "Boost days earned"}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
